'use client'

import { createContext, useContext, useEffect, useMemo, useState } from 'react'

/**
 * "Is a page showing its sticky mobile commerce bar right now?" — the one
 * piece of state two unrelated fixed-position layers both need.
 *
 * The PDP's buy bar (`ProductDetail`) and the reviews floating layer
 * (`ReviewsFloatingHost`) are rendered from different trees — the bar from
 * page `children`, the floater from the root layout — so neither can see the
 * other in CSS or in props. Both pin themselves to the bottom of the viewport,
 * so on a narrow screen they land on top of each other. This context is the
 * app-layer channel between them: the page DECLARES its bar, the floater READS
 * that and steps aside.
 *
 * Deliberately app-layer, not a `data-` attribute on `<body>` and not a
 * shared-ui concern: a DOM attribute set from an effect is ambiguous across
 * SSR/hydration, and shared-ui components stay dumb (they take a prop, they
 * don't reach for global state). Stacking contract this serves — see
 * docs/reference/storefront.md → "Fixed-element stacking ladder".
 */
type CommerceBarValue = {
  visible: boolean
  setVisible: (visible: boolean) => void
}

// Default is "no bar", so a component rendered outside the provider (a test,
// a preview route) behaves exactly as it did before this context existed.
const CommerceBarContext = createContext<CommerceBarValue>({
  visible: false,
  setVisible: () => {},
})

export function CommerceBarProvider({ children }: { children: React.ReactNode }) {
  const [visible, setVisible] = useState(false)
  const value = useMemo(() => ({ visible, setVisible }), [visible])
  return <CommerceBarContext.Provider value={value}>{children}</CommerceBarContext.Provider>
}

/**
 * Read: is a sticky commerce bar mounted right now?
 *
 * NOTE this says nothing about the viewport. The PDP bar is `lg:hidden`, so it
 * is in the DOM at every width and *visible* only below `lg`. A consumer that
 * moves out of its way must match that breakpoint rather than hiding outright,
 * or it will disappear on desktop where there was never a collision.
 */
export function useCommerceBarVisible(): boolean {
  return useContext(CommerceBarContext).visible
}

/**
 * Declare: this page's sticky commerce bar is (or is not) mounted. Clears on
 * unmount so navigating away from a PDP can't leave the flag stuck on.
 */
export function useDeclareCommerceBar(visible: boolean): void {
  const { setVisible } = useContext(CommerceBarContext)
  useEffect(() => {
    setVisible(visible)
    return () => setVisible(false)
  }, [visible, setVisible])
}
